import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { FlaskConical } from "lucide-react";
import { api } from "@/lib/api";
import { Card, SectionTitle } from "@/components/shared/primitives";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const RULE_TYPES = ["url", "click", "element", "upload"];

export default function FeatureMaps() {
  const { data: apps } = useQuery({ queryKey: ["apps-all"], queryFn: () => api.get("/apps") });
  const [appId, setAppId] = useState("");
  const { data: features, refetch } = useQuery({
    queryKey: ["features", appId],
    queryFn: () => api.get("/features", { app_id: appId }),
    enabled: !!appId,
  });

  const [featureId, setFeatureId] = useState("");
  const [type, setType] = useState("url");
  const [matchText, setMatchText] = useState('{\n  "path": "/research"\n}');

  const parseMatch = () => {
    try {
      return JSON.parse(matchText);
    } catch {
      toast.error("Match must be valid JSON.");
      return null;
    }
  };

  const test = async () => {
    const match = parseMatch();
    if (!match) return;
    try {
      const res = await api.post("/rules/test", { type, match });
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const save = async () => {
    if (!featureId) return toast.error("Pick a feature.");
    const match = parseMatch();
    if (!match) return;
    try {
      await api.post("/rules", { feature_id: featureId, type, match });
      toast.success("Rule saved.");
      refetch();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="p-5">
        <SectionTitle className="mb-1">Feature maps</SectionTitle>
        <p className="text-[13px] text-nb-ink2 mb-4">
          Rules may only match on interface elements, a label, role, test id or URL path, never on page text,
          input values, document names or query strings.
        </p>
        <Select value={appId} onValueChange={(v) => { setAppId(v); setFeatureId(""); }}>
          <SelectTrigger className="w-[220px]" data-testid="fm-app-select"><SelectValue placeholder="Choose a tool" /></SelectTrigger>
          <SelectContent>{(apps?.data || []).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
        </Select>

        {features && (
          <div className="mt-5 space-y-3">
            {features.data.map((f) => (
              <div key={f.id} className="border border-nb-border rounded-lg p-3" data-testid={`feature-${f.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[14px] font-medium text-nb-ink">{f.name}</span>
                  <span className="text-[12.5px] text-nb-ink2">{f.rules.length} rule{f.rules.length !== 1 ? "s" : ""}</span>
                </div>
                {f.rules.map((r) => (
                  <div key={r.id} className="mt-2 text-[12.5px] text-nb-ink2 font-mono bg-nb-border/20 rounded p-2">
                    [{r.type}] {JSON.stringify(r.match)} · v{r.version}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </Card>

      {appId && features && (
        <Card className="p-5">
          <SectionTitle className="mb-3">Add or update a rule</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-[12.5px] text-nb-ink2 block mb-1">Feature</label>
              <Select value={featureId} onValueChange={setFeatureId}>
                <SelectTrigger data-testid="rule-feature"><SelectValue placeholder="Choose a feature" /></SelectTrigger>
                <SelectContent>{features.data.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[12.5px] text-nb-ink2 block mb-1">Type</label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="rule-type"><SelectValue /></SelectTrigger>
                <SelectContent>{RULE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <label className="text-[12.5px] text-nb-ink2 block mb-1">Match (JSON)</label>
          <Textarea data-testid="rule-match" value={matchText} onChange={(e) => setMatchText(e.target.value)} rows={5} className="font-mono text-[13px]" />
          <div className="flex gap-2 mt-3">
            <Button data-testid="rule-test-btn" variant="outline" className="rounded-full gap-2" onClick={test}><FlaskConical size={15} /> Test this rule</Button>
            <Button data-testid="rule-save-btn" className="rounded-full bg-nb-sage hover:bg-nb-sage/90 text-white" onClick={save}>Save rule</Button>
          </div>
        </Card>
      )}
    </div>
  );
}
